import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import CTA from '../components/CTA';
import NotFound from './NotFound';
import posts, { postList } from '../data/posts';
import './BlogPost.css';

// Convert date to ISO 8601 (YYYY-MM-DD), falling back to today
const parseDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return new Date().toISOString().split('T')[0];
  return date.toISOString().split('T')[0];
};

const ReadingProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return <div className="reading-progress" style={{ transform: `scaleX(${progress})` }} aria-hidden="true" />;
};

const BlogPost = () => {
  const { id } = useParams();
  const post = posts[parseInt(id, 10)];

  if (!post) {
    return (
      <NotFound
        title="Blog post not found"
        message="The blog post you're looking for doesn't exist."
        backTo="/blogs"
        backLabel="Back to blog"
      />
    );
  }

  const publishedDate = parseDate(post.date);
  const modifiedDate = publishedDate; // Can be updated if content is modified

  const blogPostStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: 'https://escr.online/og-image.jpg',
    datePublished: publishedDate,
    dateModified: modifiedDate,
    author: {
      '@type': 'Organization',
      name: 'ESCR LLC',
      url: 'https://escr.online'
    },
    publisher: {
      '@type': 'Organization',
      name: 'ESCR LLC',
      logo: {
        '@type': 'ImageObject',
        url: 'https://escr.online/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://escr.online/blogs/${post.id}`
    },
    articleSection: post.category,
    keywords: post.category,
    url: `https://escr.online/blogs/${post.id}`,
    inLanguage: 'en-US'
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://escr.online' },
      { '@type': 'ListItem', position: 2, name: 'Blogs', item: 'https://escr.online/blogs' },
      { '@type': 'ListItem', position: 3, name: post.title, item: `https://escr.online/blogs/${post.id}` }
    ]
  };

  const structuredData = [blogPostStructuredData, breadcrumbStructuredData];

  const index = postList.findIndex((p) => p.id === post.id);
  const newer = postList[index - 1];
  const older = postList[index + 1];

  return (
    <div className="blog-post">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category}, software development, ${post.title.toLowerCase()}`}
        ogType="article"
        structuredData={structuredData}
        canonicalUrl={`https://escr.online/blogs/${post.id}`}
      />
      <ReadingProgress />

      <header className="post-hero">
        <div className="grid-bg" aria-hidden="true" />
        <div className="container post-narrow">
          <Link to="/blogs" className="back-link">
            <Icon name="arrowLeft" />
            All articles
          </Link>
          <div className="post-meta">
            <span className="tag tag-accent">{post.category}</span>
            <span className="post-meta-text">
              <Icon name="clock" />
              {post.readTime}
            </span>
          </div>
          <h1 className="post-title">{post.title}</h1>
          <p className="post-excerpt">{post.excerpt}</p>
          <div className="post-byline">
            <span className="post-avatar" aria-hidden="true">E</span>
            <span>
              <span className="post-author">ESCR Engineering</span>
              <time className="post-date" dateTime={publishedDate}>
                {post.date}
              </time>
            </span>
          </div>
        </div>
      </header>

      <section className="section section-tight">
        <div className="container post-narrow">
          <article className="post-article" itemScope itemType="https://schema.org/BlogPosting">
            <meta itemProp="headline" content={post.title} />
            <meta itemProp="datePublished" content={publishedDate} />
            <div className="prose" dangerouslySetInnerHTML={{ __html: post.content }} itemProp="articleBody" />
          </article>

          <nav className="post-nav" aria-label="More articles">
            {older ? (
              <Link to={`/blogs/${older.id}`} className="card post-nav-card">
                <span className="post-nav-label">
                  <Icon name="arrowLeft" /> Previous
                </span>
                <span className="post-nav-title">{older.title}</span>
              </Link>
            ) : (
              <span />
            )}
            {newer && (
              <Link to={`/blogs/${newer.id}`} className="card post-nav-card post-nav-next">
                <span className="post-nav-label">
                  Next <Icon name="arrowRight" />
                </span>
                <span className="post-nav-title">{newer.title}</span>
              </Link>
            )}
          </nav>
        </div>
      </section>

      <CTA
        title="Want this kind of thinking on your project?"
        secondary={{ to: '/blogs', label: 'More articles' }}
      />
    </div>
  );
};

export default BlogPost;
